/**
 * App — roteamento por hash dentro do Shell novo (U1: sidebar + topbar com
 * seletor global de competência). A raiz "#/" é a lista de competências até o
 * U2 (dashboard); o rótulo fica para a página.
 *
 * Autenticação (senha fixa — demo na VPS): sem token válido, a tela é SEMPRE a
 * de login (neutra, sem identificação da aplicação). Com token, qualquer 401 da
 * API derruba de volta para o login — a rota fica no hash e se recupera sozinha
 * ao entrar de novo. Título da aba FIXO e neutro em todas as telas (nada que
 * identifique a aplicação).
 */
import { useEffect, useState } from "react";
import { useRota } from "./roteador";
import { Shell } from "./componentes/Shell";
import { HomePage } from "./paginas/HomePage";
import { CompetenciasPage } from "./paginas/CompetenciasPage";
import { CompetenciaPage } from "./paginas/CompetenciaPage";
import { FuncionariosPage } from "./paginas/FuncionariosPage";
import { AdmissaoWizard } from "./paginas/AdmissaoWizard";
import { FuncionarioPage } from "./paginas/FuncionarioPage";
import { ParametrosPage } from "./paginas/ParametrosPage";
import { PerfisPage } from "./paginas/PerfisPage";
import { CatalogoPage } from "./paginas/CatalogoPage";
import { AuditoriaPage } from "./paginas/AuditoriaPage";
import { BancoHorasPage } from "./paginas/BancoHorasPage";
import { EmprestimosPage } from "./paginas/EmprestimosPage";
import { TomadoresPage } from "./paginas/TomadoresPage";
import { BeneficiosPage } from "./paginas/BeneficiosPage";
import { FeriasPage } from "./paginas/FeriasPage";
import { FeriasColetivasPage } from "./paginas/FeriasColetivasPage";
import { ReciboFeriasPage } from "./paginas/ReciboFeriasPage";
import { DecimoPage } from "./paginas/DecimoPage";
import { RescisaoPage } from "./paginas/RescisaoPage";
import { IntegracaoConveniaPage } from "./paginas/IntegracaoConveniaPage";
import { IntegracaoTiqueTaquePage } from "./paginas/IntegracaoTiqueTaquePage";
import { AdminPage } from "./paginas/AdminPage";
import { LoginPage } from "./paginas/LoginPage";
import { api, tokenAtual, limparToken, aoReceber401 } from "./api";

/** Título da aba: FIXO e neutro em todas as telas (pedido do dono — nada de
 *  marca nem vocabulário do domínio na aba do navegador). */
const TITULO_ABA = "Painel";

export function App() {
  const partes = useRota();
  const [fase, setFase] = useState<"verificando" | "login" | "dentro">("verificando");

  // título neutro em TODAS as telas (inclusive o login)
  useEffect(() => {
    document.title = TITULO_ABA;
  }, []);

  // portão de autenticação: valida o token guardado no boot; qualquer 401 da
  // API (token expirado/inválido) limpa e volta para o login
  useEffect(() => {
    aoReceber401(() => {
      limparToken();
      setFase("login");
    });
    if (!tokenAtual()) {
      setFase("login");
      return () => aoReceber401(null);
    }
    let vivo = true;
    // validação do boot: uma rota PROTEGIDA qualquer (401 → o callback limpa o
    // token e mostra o login; erro de rede cai no catch → login também)
    api
      .get("/competencias")
      .then(() => {
        if (vivo) setFase("dentro");
      })
      .catch(() => {
        if (vivo) setFase("login");
      });
    return () => {
      vivo = false;
      aoReceber401(null);
    };
  }, []);

  if (fase === "verificando") return null;
  if (fase === "login") {
    return (
      <LoginPage
        onEntrar={() => {
          // a rota desejada continua no hash — o usuário volta para onde estava
          setFase("dentro");
        }}
      />
    );
  }

  let conteudo: React.ReactNode;
  if (partes[0] === "competencias" && partes[1]) {
    conteudo = <CompetenciaPage id={Number(partes[1])} />;
  } else if (partes[0] === "competencias") {
    conteudo = <CompetenciasPage />;
  } else if (partes.length === 0) {
    conteudo = <HomePage />;
  } else if (partes[0] === "funcionarios" && partes[1] === "novo") {
    conteudo = <AdmissaoWizard />;
  } else if (partes[0] === "funcionarios" && partes[1]) {
    conteudo = <FuncionarioPage id={partes[1]} />;
  } else if (partes[0] === "funcionarios") {
    conteudo = <FuncionariosPage />;
  } else if (partes[0] === "beneficios") {
    conteudo = <BeneficiosPage />;
  } else if (partes[0] === "ferias" && partes[1]) {
    conteudo = <ReciboFeriasPage id={partes[1]} />;
  } else if (partes[0] === "ferias") {
    conteudo = <FeriasPage />;
  } else if (partes[0] === "ferias-coletivas" && partes[1]) {
    conteudo = <FeriasColetivasPage id={partes[1]} />;
  } else if (partes[0] === "ferias-coletivas") {
    conteudo = <FeriasColetivasPage />;
  } else if (partes[0] === "decimo") {
    conteudo = <DecimoPage />;
  } else if (partes[0] === "rescisao") {
    conteudo = <RescisaoPage />;
  } else if (partes[0] === "parametros") {
    conteudo = <ParametrosPage />;
  } else if (partes[0] === "perfis") {
    conteudo = <PerfisPage />;
  } else if (partes[0] === "catalogo") {
    conteudo = <CatalogoPage />;
  } else if (partes[0] === "auditoria") {
    conteudo = <AuditoriaPage />;
  } else if (partes[0] === "banco-horas") {
    conteudo = <BancoHorasPage funcionarioId={partes[1]} />;
  } else if (partes[0] === "emprestimos") {
    conteudo = <EmprestimosPage />;
  } else if (partes[0] === "tomadores") {
    conteudo = <TomadoresPage />;
  } else if (partes[0] === "integracoes" && partes[1] === "convenia") {
    conteudo = <IntegracaoConveniaPage />;
  } else if (partes[0] === "integracoes" && partes[1] === "tiquetaque") {
    conteudo = <IntegracaoTiqueTaquePage />;
  } else if (partes[0] === "admin") {
    conteudo = <AdminPage />;
  } else {
    conteudo = <p>Rota desconhecida: {partes.join("/")}</p>;
  }

  return <Shell partes={partes}>{conteudo}</Shell>;
}
